import { useEffect, useState } from "react";
import { api } from "./api";

// Count of active MCP grants (consented, not disconnected). Reuses the
// existing /connected-apps endpoint; the in-flight promise is memoized
// so the sidebar badge and the dashboard tile share one request.
// resetConnectedCount() drops the memo and re-fetches every live
// consumer — called after a revoke so the badge/tile update without a
// reload.
let cache: Promise<number> | null = null;
const subscribers = new Set<() => void>();

function load(): Promise<number> {
  if (!cache) {
    cache = api
      .connectedApps()
      .then((r) => r.grants.length)
      .catch(() => 0);
  }
  return cache;
}

export function resetConnectedCount(): void {
  cache = null;
  subscribers.forEach((reload) => reload());
}

// Returns null until the first fetch resolves, then the live grant count.
// Callers use the null to distinguish "still loading" from a genuine zero
// (e.g. the dashboard's Agents tile only shows its "connect an agent"
// nudge once it knows the count is really 0, not merely unfetched).
export function useConnectedCount(): number | null {
  const [n, setN] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    const reload = () => {
      void load().then((v) => {
        if (alive) setN(v);
      });
    };
    reload();
    subscribers.add(reload);
    return () => {
      alive = false;
      subscribers.delete(reload);
    };
  }, []);
  return n;
}
